<script lang="ts">
  /**
   * A strip of favorites or a bank's slots across the bottom of the screen, scrolling sideways.
   * Click to load (in preview mode it goes to the preview). Shown and hidden with J.
   */
  import type { AppController } from '../../app/controller.svelte';
  import { SLOTS } from '../../core/presets';
  import Icon from './Icon.svelte';

  let { ctl }: { ctl: AppController } = $props();
  let source = $state<'favorites' | 'bank'>('favorites');
  const favorites = $derived(ctl.favorites());
  const slots = $derived(ctl.presetsInBank(ctl.bank));
  const keyLabel = (s: number) => (s === 9 ? '0' : String(s + 1));
  let scroller = $state<HTMLDivElement>();

  // A vertical mouse wheel scrolls the strip sideways.
  function onwheel(e: WheelEvent) {
    if (!scroller || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
    scroller.scrollLeft += e.deltaY;
    e.preventDefault();
  }
</script>

<div class="strip" data-testid="preset-strip" data-tour="strip">
  <div class="bar">
    <div class="seg" role="group" aria-label="Show">
      <button
        class:active={source === 'favorites'}
        aria-pressed={source === 'favorites'}
        data-testid="strip-favorites"
        onclick={() => (source = 'favorites')}><Icon name="heart-filled" /> Favorites</button
      >
      <button
        class:active={source === 'bank'}
        aria-pressed={source === 'bank'}
        data-testid="strip-bank"
        onclick={() => (source = 'bank')}>Banks</button
      >
    </div>
    {#if source === 'bank'}
      <button class="icon-btn" aria-label="Previous bank" onclick={() => ctl.setBank(ctl.bank - 1)}
        ><Icon name="chevron-left" /></button
      >
      <span class="bank-name">{ctl.libraryVersion >= 0 ? ctl.library.bankName(ctl.bank) : ''}</span>
      <button class="icon-btn" aria-label="Next bank" onclick={() => ctl.setBank(ctl.bank + 1)}
        ><Icon name="chevron-right" /></button
      >
    {/if}
    <span class="grow"></span>
    <button
      class="icon-btn"
      aria-label="Hide the strip"
      title={`Hide${ctl.keyHint('ui.strip')}`}
      onclick={() => ctl.setPane('strip', false)}><Icon name="close" /></button
    >
  </div>
  <div class="tiles" bind:this={scroller} {onwheel}>
    {#if source === 'favorites'}
      {#each favorites as p (p.id)}
        <button
          class="tile"
          class:current={p.id === ctl.currentPresetId}
          title={p.name}
          data-testid="strip-tile"
          style:background-image={p.thumbnail ? `url(${p.thumbnail})` : undefined}
          onclick={() => ctl.applyPreset(p)}><span class="label">{p.name}</span></button
        >
      {:else}
        <p class="empty">
          No favorites yet: press <Icon name="heart" />{ctl
            .keyHint('favorite.add')
            .replace(' (', ' (or ')} on a look you like.
        </p>
      {/each}
    {:else}
      {#each Array.from({ length: SLOTS }, (_, i) => i) as s (s)}
        {@const p = slots[s] ?? null}
        <button
          class="tile"
          class:current={p && p.id === ctl.currentPresetId}
          class:empty-slot={!p}
          title={p ? `${p.name} (key ${keyLabel(s)})` : `Empty: Shift+${keyLabel(s)} saves here`}
          data-testid="strip-tile"
          style:background-image={p?.thumbnail ? `url(${p.thumbnail})` : undefined}
          onclick={() => (p ? ctl.recall(ctl.bank, s) : ctl.store(ctl.bank, s))}
          ><span class="key">{keyLabel(s)}</span><span class="label"
            >{#if p}{p.name}{:else}<Icon name="plus" />{/if}</span
          ></button
        >
      {/each}
    {/if}
  </div>
</div>

<style>
  .strip {
    pointer-events: auto;
    width: 100%;
    padding: 6px 8px 8px;
    border-radius: var(--radius);
    background: rgba(10, 10, 16, 0.86);
    border: 1px solid var(--border);
    backdrop-filter: blur(10px);
  }
  .bar {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 6px;
  }
  .seg {
    display: flex;
    gap: 4px;
  }
  .seg button {
    font-size: 11.5px;
    padding: 2px 10px;
  }
  .bank-name {
    font-size: 12px;
    color: var(--muted);
    min-width: 70px;
    text-align: center;
  }
  .tiles {
    display: flex;
    gap: 8px;
    overflow-x: auto;
    scroll-snap-type: x proximity;
    scrollbar-width: thin;
    padding-bottom: 2px;
  }
  .tile {
    position: relative;
    flex: 0 0 auto;
    width: 128px;
    height: 72px;
    padding: 0;
    border: 2px solid transparent;
    border-radius: var(--radius-sm);
    background: rgba(255, 255, 255, 0.05) center / cover no-repeat;
    scroll-snap-align: start;
    overflow: hidden;
  }
  .tile.current {
    border-color: var(--accent);
  }
  .tile.empty-slot {
    border: 1px dashed var(--border);
  }
  .label {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 2px 6px;
    font-size: 11px;
    text-align: left;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.75));
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .key {
    position: absolute;
    top: 3px;
    left: 5px;
    font-size: 11px;
    font-family: var(--mono);
    color: var(--muted);
  }
  .empty {
    margin: 6px 4px;
    font-size: 12px;
    color: var(--muted);
  }
</style>
